import type { DocumentKind, PaymentMethod } from "../api/types.js";

// Analytics from the post-booking documents page when it is embedded.
export type DocsEvent =
  | { name: "docs_payment_selected"; method: PaymentMethod }
  // `when`: "now" = uploaded from the embedded post-booking prompt; "later"
  // = from the email link. Only "now" is ever posted (the email-link page is
  // standalone and posts nothing); the field mirrors the spec's event shape.
  | { name: "docs_uploaded"; kind: DocumentKind; when: "now" | "later" };

export type IframeMessage =
  | { type: "booking-complete"; confirmationId: string; documentsUploadUrl?: string }
  | ({ type: "docs-event" } & DocsEvent)
  | { type: "booking-error";    message: string; code?: string }
  | { type: "resize";           height: number }
  // Asks the parent to scroll its viewport to the top. We send this when
  // the user lands on the post-booking confirmation so they don't end up
  // staring at the middle of the marketing page below where the iframe
  // had been showing the form.
  | { type: "scroll-to-top" };

// Compile-time allowlist of parent origins we trust to receive booking
// confirmation / error / resize messages. window.postMessage's targetOrigin
// only delivers when the parent's actual origin matches, so iterating over
// this list is safe — at most one of these calls actually delivers, the
// rest are no-ops. This keeps confirmation IDs (and server error strings)
// out of the hands of any other parent that might be allowed by CSP.
//
// Sourced from VITE_ALLOWED_PARENT_ORIGINS (comma-separated) so prod and
// nonprod builds carry only the origins relevant to their environment.
// Falls back to https://pricare.health (prod default) when the env var is
// unset (local dev, tests). postMessage's targetOrigin doesn't support
// wildcards, so preview-branch deploys of the marketing Pages project
// (https://<hash>.pricare-dev.pages.dev) won't receive messages — list
// them individually if needed.
const RAW_ORIGINS: string =
  (import.meta as { env?: Record<string, string | undefined> }).env
    ?.VITE_ALLOWED_PARENT_ORIGINS ?? "https://pricare.health";

export const ALLOWED_PARENT_ORIGINS: readonly string[] = RAW_ORIGINS.split(",")
  .map((s) => s.trim())
  .filter(Boolean);

function inIframe(): boolean {
  return typeof window !== "undefined" && window.parent !== window;
}

function send(msg: IframeMessage): void {
  if (!inIframe()) return;
  for (const origin of ALLOWED_PARENT_ORIGINS) {
    window.parent.postMessage(msg, origin);
  }
}

export function postBookingComplete(
  confirmationId: string,
  documentsUploadUrl?: string,
): void {
  send(
    documentsUploadUrl === undefined
      ? { type: "booking-complete", confirmationId }
      : { type: "booking-complete", confirmationId, documentsUploadUrl },
  );
}

export function postDocsEvent(event: DocsEvent): void {
  send({ type: "docs-event", ...event });
}

export function postBookingError(message: string, code?: string): void {
  send(code === undefined ? { type: "booking-error", message } : { type: "booking-error", message, code });
}

// Ask the parent page to scroll to the top, AND scroll our own iframe
// document if we're standalone (parent === self). Belt-and-suspenders so
// the right thing happens in both embedded and direct-view contexts.
export function postScrollToTop(): void {
  send({ type: "scroll-to-top" });
  if (typeof window !== "undefined") {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }
}

const MIN_HEIGHT = 600;

export function startResizeObserver(): () => void {
  if (typeof window === "undefined") return () => {};

  let last = 0;
  let raf = 0;

  function measure(): number {
    // documentElement.scrollHeight is the most reliable cross-browser way
    // to get total content height — it grows with content even when the
    // iframe is fixed-size, which is exactly when the parent needs the
    // updated number to resize.
    return Math.max(
      MIN_HEIGHT,
      Math.ceil(document.documentElement.scrollHeight),
      Math.ceil(document.body?.scrollHeight ?? 0),
    );
  }

  function flush() {
    raf = 0;
    const height = measure();
    if (height === last) return;
    last = height;
    send({ type: "resize", height });
  }

  function schedule() {
    if (raf) return;
    raf = requestAnimationFrame(flush);
  }

  // Initial measurement so the parent doesn't sit at its 900px default.
  flush();

  // ResizeObserver: catches when our own box size changes (uncommon in a
  // fixed-size iframe, but still relevant on desktop where the parent may
  // give us its full viewport).
  const ro =
    typeof ResizeObserver !== "undefined" ? new ResizeObserver(schedule) : null;
  ro?.observe(document.documentElement);
  if (document.body) ro?.observe(document.body);

  // MutationObserver: catches DOM additions/removals (file uploads, error
  // banners, "Redirecting…" swap, Place autocomplete element mount, etc.)
  // that change scrollHeight WITHOUT changing the documentElement's border
  // box. This is the critical observer for the iframe-auto-resize case.
  const mo =
    typeof MutationObserver !== "undefined" ? new MutationObserver(schedule) : null;
  mo?.observe(document.documentElement, {
    childList: true,
    subtree: true,
    attributes: true,
    characterData: true,
  });

  window.addEventListener("load", schedule);
  window.addEventListener("resize", schedule);

  return () => {
    if (raf) cancelAnimationFrame(raf);
    ro?.disconnect();
    mo?.disconnect();
    window.removeEventListener("load", schedule);
    window.removeEventListener("resize", schedule);
  };
}
